<template>
    <div class="new-multi-select-dropdown rfx-new-dropdown">
        <label class="new-multi-select-dropdown__search">
            <base-material-design-icon class="new-multi-select-dropdown__search-icon"
                icon="search" />
            <input class="new-multi-select-dropdown__search-input"
                :placeholder="searchPlaceholder || ''"
                v-model="searchTerm" />
            <base-material-design-icon class="new-multi-select-dropdown__search-clear"
                @click="clearSearch"
                icon="close" />
        </label>
        <template v-if="filteredOptions.length">
            <span v-if="listTitle"
                class="new-multi-select-dropdown__list-title">{{ listTitle }}</span>
            <ul class="new-multi-select-dropdown__list">
                <li v-for="option in filteredOptions"
                    class="new-multi-select-dropdown__option"
                    :class="{ 'new-multi-select-dropdown__option_selected': selection.includes(option.value) }"
                    :key="option.value"
                    @click.stop="handleClick(option)">
                    <base-material-design-icon icon="check"
                        class="new-multi-select-dropdown__icon" />
                    {{ option.label }}
                </li>
            </ul>
        </template>
        <div v-else
            class="new-multi-select-dropdown__list-placeholder">Nothing found</div>
        <div class="new-multi-select-dropdown__controls">
            <button class="new-multi-select-dropdown__clear"
                @click="clearSelection">Clear</button>
            <button class="new-multi-select-dropdown__apply"
                @click="applySelection">Apply</button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'NewSelectDropdown',
    emits: ['selection-changed', 'selection-applied'],
    props: {
        /*
            {
                label: string,
                value: string
            }[]
        */
        options: {
            type: Array,
            default: () => []
        },
        /* Array of values (strings) */
        values: {
            type: Array,
            default: []
        },
        searchPlaceholder: String,
        listTitle: String,
    },
    data() {
        return {
            searchTerm: '',
            selection: [...this.values]
        }
    },
    computed: {
        trimmedSearchTerm() {
            return this.searchTerm.trim();
        },
        filteredOptions() {
            if (!this.trimmedSearchTerm) {
                return this.options;
            }

            const loweredSearchTerm = this.trimmedSearchTerm.toLowerCase();

            return this.options.filter(option => option.label.toLowerCase().includes(loweredSearchTerm));
        }
    },
    watch: {
        values: {
            handler() {
                this.selection = [...this.values];
            },
            deep: true
        }
    },
    methods: {
        clearSearch() {
            this.searchTerm = '';
        },
        clearSelection() {
            this.selection = [];
            this.clearSearch();
            this.$emit('selection-changed', this.selection);
        },
        handleClick(option) {
            const isOptionSelected = this.selection.includes(option.value);
            if (isOptionSelected) {
                this.selection = this.selection.filter(value => value !== option.value);
            } else {
                this.selection = [...this.selection, option.value];
            }
            this.$emit('selection-changed', this.selection);
        },
        applySelection() {
            this.$emit('selection-applied', this.selection);
        }
    }
}
</script>

<style lang="scss">
.new-multi-select-dropdown {
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;

    &__search {
        display: flex;
        gap: 8px;
        align-items: center;
        border-bottom: 1px solid $rfx-border-color;
        padding: 8px 0;
        margin: 0 8px;
    }

    &__search-icon {
        color: $rfx-steel;
        font-size: 18px;
    }

    &__search-input {
        flex: 1 1 100%;
        border: none;
        font-size: 14px;
        outline: none;

        &::placeholder {
            color: $rfx-silver;
        }
    }

    &__list-title {
        font-size: 10px;
        line-height: 12px;
        color: $rfx-steel;
        display: block;
        padding: 4px 8px;
        margin-bottom: -8px;
    }

    &__list {
        display: flex;
        flex-direction: column;
        list-style-type: none;
        margin: 0 -12px;
        padding: 0;
        overflow-y: auto;
    }

    &__option {
        display: flex;
        gap: 4px;
        padding: 8px 20px;
        align-items: center;
        border-radius: 2px;
        min-width: 280px;
        box-sizing: border-box;
        cursor: pointer;

        &:hover {
            background-color: $rfx-background-color;
        }

        &_selected {
            .new-multi-select-dropdown__icon {
                opacity: 1;
            }
        }
    }

    &__list-placeholder {
        display: flex;
        height: 50px;
        min-width: 256px;
        align-items: center;
        justify-content: center;
        color: $rfx-silver;
    }

    &__icon {
        opacity: 0;
        font-size: 14px;
    }

    &__controls {
        padding-top: 8px;
        display: flex;
        justify-content: flex-end;
        gap: 4px;
    }

    &__clear,
    &__apply {
        border: none;
        outline: none;
        padding: 8px 12px;
        border-radius: 3px;
        font-size: 10px;
        line-height: 12px;
        font-weight: 700;
        text-transform: uppercase;
        cursor: pointer;

        &:disabled {
            opacity: .75;
        }
    }

    &__clear {
        background-color: transparent;
        color: $rfx-steel;
    }

    &__apply {
        background-color: $rfx-quarter-march;
        color: #fff;
    }
}
</style>